import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import {
    ICarrierRoutingConfig,
    ICarrierRoutingConfigUpdate,
    carrierRoutingConfigsAtom,
    carrierRoutingConfigsLoadingAtom
} from "./carrier_routing_config_types";

export interface ICarrierCodeOption {
    code: string;
    name: string;
}

type UseCarrierRoutingConfigs = [ICarrierRoutingConfig[], {
    list: () => Promise<ICarrierRoutingConfig[] | null>;
    update: (id: number, input: ICarrierRoutingConfigUpdate) => Promise<ICarrierRoutingConfig | null>;
    listServices: (carrierCode: string) => Promise<ICarrierCodeOption[] | null>;
    listPackages: (carrierCode: string) => Promise<ICarrierCodeOption[] | null>;
}];

// #region useCarrierRoutingConfigs
export const useCarrierRoutingConfigs = (): UseCarrierRoutingConfigs => {
    const [items, setItems] = useAtom(carrierRoutingConfigsAtom);
    const setLoading = useSetAtom(carrierRoutingConfigsLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const headers = { 'X-Wallmates-Auth': authToken };

    // #region list
    const list = async (): Promise<ICarrierRoutingConfig[] | null> => {
        try {
            setLoading(true);
            const response = await axios.get(`${environment.baseUrl}/carrier-routing-config`, { headers });
            setItems(response.data);
            return response.data as ICarrierRoutingConfig[];
        } catch (e: any) {
            errorCatcher(e, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to fetch carrier routing configs.' });
            return null;
        } finally {
            setLoading(false);
        }
    };

    // #region update
    const update = async (id: number, input: ICarrierRoutingConfigUpdate): Promise<ICarrierRoutingConfig | null> => {
        try {
            const response = await axios.put(`${environment.baseUrl}/carrier-routing-config/${id}`, input, { headers });
            await list();
            return response.data as ICarrierRoutingConfig;
        } catch (e: any) {
            errorCatcher(e, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to update carrier routing config.' });
            return null;
        }
    };

    // #region listServices
    const listServices = async (carrierCode: string): Promise<ICarrierCodeOption[] | null> => {
        try {
            const response = await axios.get(`${environment.baseUrl}/carrier-routing-config/services/${carrierCode}`, { headers });
            return response.data as ICarrierCodeOption[];
        } catch (e: any) {
            errorCatcher(e, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: 'error', text: `Failed to fetch services for carrier: ${carrierCode}` });
            return null;
        }
    };

    // #region listPackages
    const listPackages = async (carrierCode: string): Promise<ICarrierCodeOption[] | null> => {
        try {
            const response = await axios.get(`${environment.baseUrl}/carrier-routing-config/packages/${carrierCode}`, { headers });
            return response.data as ICarrierCodeOption[];
        } catch (e: any) {
            errorCatcher(e, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: 'error', text: `Failed to fetch packages for carrier: ${carrierCode}` });
            return null;
        }
    };

    return [items, { list, update, listServices, listPackages }];
};
